import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

// project-imports
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";
import Spinner from "../../components/spinner-component/SpinnerOne";
import { GoogleAnalyticsEnum, pageView } from '../../utils/google-analytics';
import KnowledgeCard from "../../components/knowledge-card/KnowledgeCard";
import { getKnowledgeCardFilters } from "../../utils/filter";
import KnowledgeCardAllStencil from "../../components/knowledge-card/stencils/KnowledgeCardAllStencil";
import KnowledgeArticle from "../../components/knowledge-card/KnowledgeArticle";
import { useQuickInsightContext } from "../../context/quick_insights_context";
import ErrorComponent from "../../components/error-message/ErrorComponent";

const KnowledgeCardAll = () => {

    const { isLoading, isError, quickInsights } = useQuickInsightContext();
    const navigate = useNavigate();
    const { insight } = useParams();

    // State variable initialization
    const [filterItems, setFilterItems] = useState(quickInsights);
    const [activeFilter, setActiveFilter] = useState("All");
    const [visibleItems, setVisibleItems] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [ddActive, setDdActive] = useState("");

    // Getting Gallery Filter
    let knowledgeCardFilter = getKnowledgeCardFilters();

    let allItems = quickInsights;

    useEffect(() => {
        setVisibleItems(filterItems);
    }, [activeFilter]);

    // Find index of current Tab
    const currentIndex =
        allItems &&
        allItems.length &&
        allItems?.findIndex((elm) => elm?.title === selectedArticle?.title);

    // Find  prev index
    const prevIndex = currentIndex ? currentIndex - 1 : -1;

    // Find prev Tab
    const prevData = prevIndex !== -1 ? allItems[prevIndex] : null;

    // Find next index
    const nextIndex = currentIndex ? (currentIndex + 1) % allItems?.length : +1;

    // Find next Tab
    const nextData = nextIndex ? allItems[nextIndex] : null;

    const filteredDropdownList = useMemo(() => {
        // Get all unique tags existing in your items
        const existingTags = new Set(allItems?.map((data) => data?.tags?.[0] || ""));

        return knowledgeCardFilter?.filter((filter) => {
            // Always show the "All" option if it exists
            if (filter?.label === "All") return true;

            // Only keep this filter option if it matches a tag in allItems
            return existingTags?.has(filter?.label || "");
        });
    }, [knowledgeCardFilter, allItems]);

    // Constant function
    const handleFilterDropdown = () => {
        setDdActive((prev) => !prev);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter?.label);
        let tempData;
        if (filter?.value?.toLowerCase() === knowledgeCardFilter[0]?.value?.toLowerCase()) {
            tempData = allItems;
        } else {
            tempData = allItems.filter((data) => data?.tags[0]?.toLowerCase() === filter?.value.toLowerCase());
        }
        setFilterItems(tempData);
    }

    const handleReadMore = (article) => {
        const slug = encodeURIComponent(article.title.toLowerCase().replace(/ /g, '-'));

        navigate(`/salesforce-quick-insights/${slug}`, {
            replace: false,
        });

        setSelectedArticle(article);
    };

    // 4. Close modal by removing the parameter from the URL
    const handleReadMoreClose = () => {
        setSearchParams({});
        setSelectedArticle(null);
        navigate(`/salesforce-quick-insights/all`, { replace: true });
    };

    useEffect(() => {

        if (!selectedArticle) {

            //  Extract the specific parameter (returns null if not present)
            const filteredArticles = (quickInsights || []).filter((item) => encodeURIComponent(item?.title.toLowerCase().replace(/ /g, '-')) === insight);

            if (filteredArticles && filteredArticles.length) {
                setSelectedArticle(filteredArticles[0]);
            }
        }
    }, []);

    if (isLoading) {
        // Loading stencil
        return <KnowledgeCardAllStencil data={visibleItems} />
    }

    return (
        <>
            <SEO title="Salesforce Quick Insights | Longswitch Academy" /> {/* Set SEO title */}
            {pageView(window.location.pathname, GoogleAnalyticsEnum.SALESFORCE_KNOWLEDGE_CARD_ALL)}
            <Layout>
                <BreadcrumbOne
                    title="Salesforce Quick Insights" // Breadcrumb title
                    rootUrl="/"
                    parentUrl="Home"
                    currentUrl="Salesforce Quick Insights"
                />
                <div className=" edu-pricing-area bg-image edu-section-gap bg-image">
                    <div className="container edu-section-pricing-gapBottom">
                        {isLoading && <Spinner />}
                        <div className="row y-gap-15 justify-between items-center">
                            <div className="col-lg-5">
                                <div className="section-title text-start">
                                    <span className="pre-title">Across the Salesforce ecosystem</span>
                                    <h3 className="title">Salesforce Quick Insights</h3>
                                </div>
                            </div>
                            {/* <div className="col-lg-7">
                                <div className="button-group isotop-filter filters-button-group d-flex justify-content-start justify-content-lg-end">
                                    {knowledgeCardFilter?.map((filter) => (
                                        <button
                                            onClick={() => handleFilterChange(filter)}
                                            // onClick={handleChange}
                                            key={filter.id}
                                            className={
                                                filter?.value.toLowerCase() === activeFilter
                                                    ? "is-checked"
                                                    : " "
                                            }
                                        >
                                            {filter?.label || ""}
                                        </button>
                                    ))}
                                </div>
                            </div> */}

                            <div className="col-lg-7">
                                {(!isLoading && !isError) ?
                                    <>
                                        <div className="d-flex align-items-center justify-content-start justify-content-lg-end">
                                            <div className="text-14 lh--12 fw-500 text-dark-1 mr--20">
                                                Filter by:
                                            </div>

                                            <div
                                                onClick={handleFilterDropdown}
                                                className={`dropdown js-dropdown js-category-active ${ddActive ? "-is-dd-active" : ""}`}
                                            >
                                                <div
                                                    className="dropdown__button d-flex align-items-center text-14 rounded-8 px-20 py-10 text-14 lh-12"
                                                    data-el-toggle=".js-category-toggle"
                                                    data-el-toggle-active=".js-category-active"
                                                >
                                                    <span className="js-dropdown-title fs-17">
                                                        {activeFilter || "All"}
                                                    </span>
                                                    <i className="ml--5 icon-arrow-down-s-line"></i>
                                                </div>

                                                <div
                                                    className={`toggle-element -dropdown -dark-bg-dark-2 -dark-border-white-10 js-click-dropdown js-category-toggle ${ddActive ? "-is-el-visible" : ""}`}
                                                >
                                                    <div className="text-14 y-gap-15 js-dropdown-list">
                                                        {(filteredDropdownList || [])?.map((filter) => (
                                                            <Link
                                                                onClick={() => handleFilterChange(filter)}
                                                                style={{ cursor: "pointer" }}
                                                                key={filter?.id}
                                                                className={`d-block js-dropdown-link cursor ${activeFilter === filter?.label ? "active" : ""
                                                                    }`}
                                                            >
                                                                {filter?.label || ""}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    :
                                    <></>
                                }
                            </div>


                            <div className="col-lg-12">
                                {!isLoading && isError ? (
                                    <div className="row">
                                        <ErrorComponent
                                            errorCode="500"
                                            mainError="No salesforce quick insights data are available!"
                                            subText="Currently we don't have salesforce quick insights data. Please be back later."
                                            buttonText="Back to Home"
                                            link="/"
                                        />
                                    </div>
                                ) :
                                    (!isLoading && !isError && !visibleItems?.length) ? (
                                        <div className="row">
                                            <ErrorComponent
                                                errorCode="404"
                                                mainError="Failed to get the salesforce quick insights data right now."
                                                subText="Please return to the site's homepage, It looks like nothing was found at this location"
                                                buttonText="Back to Home"
                                                link="/"
                                            />
                                        </div>

                                    ) : (
                                        <>
                                            <div className="row y-gap-30 pt--50">
                                                {
                                                    (visibleItems || []).map((item, index) => (
                                                        <div className="col-12 col-xl-4 col-lg-6" key={item.id}>
                                                            <KnowledgeCard
                                                                data={item}
                                                                onReadMore={handleReadMore}
                                                            />
                                                        </div>
                                                    ))
                                                }

                                            </div>
                                        </>
                                    )}

                            </div>
                        </div>
                    </div>
                </div>
                {/* <KnowledgeCardDetail selectedArticle={selectedArticle} setArticle={setSelectedArticle} prevData={prevData} nextData={nextData} onReadMore={handleReadMore} /> */}
                <KnowledgeArticle
                    selectedArticle={selectedArticle}
                    setArticle={setSelectedArticle}
                    prevData={prevData}
                    nextData={nextData}
                    onReadMore={handleReadMore}
                    onReadMoreClose={handleReadMoreClose}
                />
            </Layout>
        </>
    )
}

export default KnowledgeCardAll;